import { useIntl } from "react-intl";

import { useTimezone } from "@/hooks/useTimezone";

type DateTimeProps = {
  date: Date | string;
  /**
   * Optional timezone key. If not provided, uses the timezone from context.
   */
  tzKey?: string;
};

/**
 * Displays a date and time in the format "MMM dd, yy @ h:mm am/pm"
 * Example: "Jan 28, 26 @ 3:45pm"
 *
 * Uses the timezone from the TimezoneContext by default, but can be overridden
 * with the tzKey prop.
 */
export const DateTime = ({ date, tzKey }: DateTimeProps) => {
  const intl = useIntl();
  const { tz } = useTimezone();

  const timezone = tzKey ?? tz.key;
  const dateObj = typeof date === "string" ? new Date(date) : date;

  const datePart = intl.formatDate(dateObj, {
    timeZone: timezone,
    month: "short",
    day: "2-digit",
    year: "2-digit",
  });

  const timePart = intl
    .formatDate(dateObj, {
      timeZone: timezone,
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    })
    .toLowerCase();

  return <>{`${datePart} @ ${timePart}`}</>;
};

DateTime.displayName = "DateTime";
